import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import { useChannel } from '@/lib/realtime/useChannel';
import { useDealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';
import { aiApi } from '../services/aiApi';
import type { AIJobStatusResponse } from '@marine/contracts/types/ai.types';

/**
 * Phase 40-03 UX-06 — stream-driven AI job status.
 *
 * The pre-40-03 implementation polled `/ai/jobs/:id` every 2s until the
 * status flipped to `completed` or `failed`. Phase 40-03 replaces that with
 * a Centrifugo subscription to `tenant.{tenantId}.ai-stream.{jobId}` —
 * terminal events (`done`, `failed`) update the TanStack Query cache via
 * `setQueryData`, which is what the 8 consumer hooks read from.
 *
 * The outer `useQuery` shape is preserved intentionally — the return type
 * is still `UseQueryResult<AIJobStatusResponse>` so spec-lookup, image-
 * analysis, comparables, description, location-content, etc. continue to
 * read `.data?.status` / `.data?.result` / `.isLoading` without edits.
 *
 * CL-7 — NO polling fallback. If a stream event never arrives (Centrifugo
 * unreachable, subscribe proxy 5xx, network partition), we fire ONE Sentry
 * `ai-job-stuck-pending` warning 30s after the hook mounts with a live
 * jobId. No UI toast, no repeated telemetry — the user's existing
 * AiOperationStatus elapsed-seconds counter communicates "still running"
 * and the Sentry warning is the ops signal to investigate.
 *
 * Reminder (memory rule `feedback_api_envelope_unwrap.md`): `api.getOne`
 * already unwraps the `{ data: ... }` envelope, so we do NOT add an
 * additional TanStack `select` that re-extracts `.data` — that would
 * double-unwrap. The query returns the bare `AIJobStatusResponse`.
 *
 * Stream event handling:
 *   - `started`        — no cache mutation (status stays whatever the
 *                        initial fetch set it to, typically 'pending')
 *   - `token`          — no cache mutation (useDescriptionStream handles
 *                        the token stream for the description feature;
 *                        other features don't care about per-token state)
 *   - `agent.step`     — no cache mutation (the `useAgentSteps` hook
 *                        consumes the dedicated agent-steps channel; this
 *                        event type never fires on `ai-stream` per CL-5)
 *   - `done`           — status → 'completed', merge payload into result
 *   - `failed`         — status → 'failed', populate error
 *
 * Race-safe: `setQueryData((prev) => prev ? merge : prev)` — if the stream
 * event lands BEFORE the initial fetch resolves (shouldn't happen on
 * flag-on path since the dispatch API seeds a pending row synchronously,
 * but guard anyway), we no-op. The initial fetch will then populate the
 * cache with the backend's eventually-consistent terminal state.
 */
export function useAIJobStatus(jobId?: string) {
  const qc = useQueryClient();
  const { data: settings } = useDealershipSettings();
  const tenantId = settings?.tenant_id ?? null;
  const eventsReceivedRef = useRef(false);

  const channel =
    tenantId && jobId ? `tenant.${tenantId}.ai-stream.${jobId}` : null;

  useChannel<Record<string, unknown>>(channel, {
    onSubscribed: () => {
      // Race-safe catch-up: the ai-engine cache-hit path can complete the
      // job in 15-50ms, publishing `done` before our WS subscribe round-trip
      // (~50-200ms) lands. Centrifugo's ai-stream namespace has
      // `history_size: 0` so late subscribers never receive past events, and
      // the outer useQuery has `staleTime: Infinity` with no polling — so
      // without this, the UI would sit on the initial `processing` snapshot
      // forever whenever cache hits.
      //
      // Invalidating on subscribed forces a fresh fetch of /ai/jobs/:id. If
      // the row is already terminal, the cache updates and downstream
      // consumers (diff table, form-population effects) fire. If still
      // processing, this is a cheap no-op that the next `done`/`failed`
      // event will replace anyway.
      eventsReceivedRef.current = true;
      qc.invalidateQueries({ queryKey: ['ai', 'jobs', jobId] });
    },
    onPublication: (env) => {
      eventsReceivedRef.current = true;
      // ai-engine publishes raw payloads at ctx.data (type + fields at the
      // top level); Laravel broadcasts wrap them in {event, payload, ...}.
      // Unwrap defensively so this hook works with both publisher shapes.
      const raw = (env as unknown) as Record<string, unknown>;
      const payload: Record<string, unknown> =
        raw && typeof raw === 'object' && 'type' in raw
          ? raw
          : ((raw as { payload?: Record<string, unknown> })?.payload ?? {});

      const eventType = payload?.type;

      if (eventType === 'done') {
        // Optimistic local patch — flips status immediately so the UI
        // transitions out of "processing" without waiting on the refetch.
        qc.setQueryData<AIJobStatusResponse>(
          ['ai', 'jobs', jobId],
          (prev) => {
            if (!prev) return prev;
            const resultPatch =
              (payload as { full_text?: string; total_tokens?: number }).full_text !== undefined
                ? {
                    ...prev.result,
                    long_description: (payload as { full_text?: string }).full_text,
                  }
                : prev.result;
            return {
              ...prev,
              status: 'completed',
              completed_at: new Date().toISOString(),
              result: resultPatch,
            };
          },
        );
        // Authoritative refetch. For streaming features (description) the
        // `done` event carries `full_text` so the patch above is enough.
        // For RPC-style features (image-analysis, spec-lookup, comparables,
        // etc.) the engine publishes a bare `{type:"done"}` and writes the
        // actual result to the AIJobStatus row via a callback. Invalidate
        // here so those consumers pull the callback-written result into the
        // cache and downstream form-population effects fire.
        qc.invalidateQueries({ queryKey: ['ai', 'jobs', jobId] });
      } else if (eventType === 'failed') {
        const errorMsg =
          typeof payload.error === 'string' ? payload.error : 'Operation failed';
        const errorCode =
          typeof payload.code === 'string' ? payload.code : null;
        const correlationId =
          typeof payload.correlation_id === 'string' ? payload.correlation_id : null;

        qc.setQueryData<AIJobStatusResponse>(
          ['ai', 'jobs', jobId],
          (prev) =>
            prev
              ? {
                  ...prev,
                  status: 'failed',
                  completed_at: new Date().toISOString(),
                  error: errorMsg,
                  error_code: errorCode,
                  correlation_id: correlationId,
                }
              : prev,
        );

        // Phase 41.3 — Sentry enrichment (D-17..D-20, SPEC §41.1-03).
        // Single-source capture point (T-41.3-02 Pitfall 1 guard): do NOT add
        // a useEffect watcher on data?.status === 'failed' — that would
        // double-capture because the subsequent refetch would re-observe the
        // same state. Stream payload is authoritative.
        const level: SeverityLevel =
          errorCode === 'provider_schema_rejected' ? 'fatal' : 'error';
        Sentry.withScope((scope) => {
          scope.setTag('ai_error_code', errorCode ?? 'unknown');
          if (correlationId) scope.setTag('ai_correlation_id', correlationId);
          scope.setContext('ai_failure', {
            job_id:         jobId,
            tenant_id:      tenantId,
            code:           errorCode,
            correlation_id: correlationId,
            source:         'ai-stream',
          });
          Sentry.captureMessage(`ai_failure:${errorCode ?? 'unknown'}`, level);
        });

        // Non-prod test sink — sentry.ts initialises with `enabled: PROD`,
        // so in dev/test the withScope + captureMessage above are no-ops.
        // Playwright reads this array to assert AC-2 + AC-3 capture tags
        // and severity levels (RESEARCH P3 Approach A).
        if (!import.meta.env.PROD && typeof window !== 'undefined') {
          const w = window as unknown as {
            __AI_SENTRY_CAPTURES__?: Array<Record<string, unknown>>;
          };
          w.__AI_SENTRY_CAPTURES__ ||= [];
          w.__AI_SENTRY_CAPTURES__.push({
            message: `ai_failure:${errorCode ?? 'unknown'}`,
            level,
            tags: {
              ai_error_code:     errorCode ?? 'unknown',
              ai_correlation_id: correlationId,
            },
            context: {
              job_id:         jobId,
              tenant_id:      tenantId,
              code:           errorCode,
              correlation_id: correlationId,
              source:         'ai-stream',
            },
          });
        }

        // NOTE: Intentionally NO qc.invalidateQueries here (T-41.3-02 Pitfall 3).
        // The stream payload is authoritative; a refetch could clobber the
        // optimistic 'failed' state with a stale Laravel 'pending' row during
        // the 50-500ms HMAC callback window before Laravel observes its own
        // failure row.
      }
      // started / token / agent.step — no cache mutation.
    },
  });

  // CL-7 stuck-pending telemetry. 30s after mount with zero stream events,
  // fire one Sentry warning. Re-armed on jobId change. Cleared on unmount
  // or when events actually arrive (since the guard reads the ref).
  useEffect(() => {
    if (!jobId) return;
    eventsReceivedRef.current = false;
    const timeout = setTimeout(() => {
      if (!eventsReceivedRef.current) {
        Sentry.captureMessage('ai-job-stuck-pending', {
          level: 'warning',
          tags: {
            jobId: jobId ?? '(none)',
            tenantId: tenantId ?? '(none)',
          },
        });
      }
    }, 30_000);
    return () => clearTimeout(timeout);
  }, [jobId, tenantId]);

  return useQuery({
    queryKey: ['ai', 'jobs', jobId],
    queryFn: () => aiApi.getJobStatus(jobId!),
    enabled: !!jobId,
    // Phase 40-03 UX-06 / CL-7 — NO polling interval key below by design.
    // Stream events drive cache updates via setQueryData; no polling
    // fallback. See the hook JSDoc for rationale + Sentry stuck-pending
    // telemetry details.
    staleTime: Infinity,
  });
}
